import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Breadcrumbs,
  Button,
  CardComponent,
  Tooltip,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React, { useEffect, useRef, useState } from 'react';
import properties from '../../core-utils/properties';
import { useNavigate, useLocation } from 'react-router-dom';
import { DOC_COMPARISON, HOME_SCREEN } from '../../core-utils/routes';
import { NoDataVisual } from '../SavingsUtilization';
import { Columns as MatrixColumns } from '@HomaHealth/ui-component-library/lib/_AIQ-components/organisms/MatrixTable';
import { observer } from 'mobx-react';
import Loader from '../../_components/molecules/Loader';
import axios from 'axios';
import useSnackbar from '../../core-utils/custom-hooks/Snackbar';
import Snackbar from '../../_components/atoms/Snackbar';
import SvgInfo from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';
import {
  createBlobUrl,
  getProvisonCellBackground,
} from '../../core-utils/helper-utils/contractAIQHelper';
import { getBooleanCell } from '../ContractDetailsPage';
import { handleDownloadCSV } from '../../core-utils/helper-utils/downloadPdfHelper';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';
import PdfHighlighter from '../../_components/organisms/PdfHighlighter';
import { DocumentDetailsDataType } from '../../core-utils/helper-utils/termCompilerHelper';
import { DottedText } from '../../core-utils/helper-utils/financialForensicsHelper';
import MatrixTable from '../../_components/organisms/MatrixTableDocComparision';
import {
  DynamicFilter,
  FilterOption,
} from '../../_components/molecules/DynamicFilter';

interface Contract {
  contract_id: string;
  eg_nid: string;
  vendor_name: string;
  employer_name: string;
  is_draft?: boolean;
}

interface ComparisonRouteState {
  doc_comparison_id: string;
}

const useStyles = makeStyles()({
  breadCrumbsGrid: {
    padding: '10px 0px 10px 63px',
  },
  hr: {
    width: '100%',
    border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    margin: 0,
  },
  root: {
    width: '1442px',
    display: 'flex',
    padding: '15px 20px 32px 20px',
    // height: '85vh',
  },
  leftView: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'auto',
    ...scrollBarStyles,
  },
  rightView: {
    width: '45%',
  },
  vendorContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  provisionGrid: {
    display: 'flex',
    alignItems: 'center',
    minWidth: '300px',
  },
  iconWrapper: {
    width: '16px',
    height: '16px',
    marginLeft: '4px',
  },
  parameterBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

const DocumentComparisonInternalPage = observer(() => {
  // Constants
  const { classes } = useStyles();
  const location = useLocation();
  const state = location.state as ComparisonRouteState;
  const navigate = useNavigate();
  const navigateToHomeScreen = () => {
    navigate(HOME_SCREEN);
  };
  const navigateToDocCompare = () => {
    navigate(DOC_COMPARISON);
  };
  const breadCrumpItemList = [
    [
      'text',
      'Home',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      navigateToHomeScreen,
    ],
    [
      'text',
      'Documents Comparison',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 600,
      },
      navigateToDocCompare,
    ],
    [
      'text',
      'Comparison Matrix',
      {
        color: patientAccessTheme.palette.text.highemphasis,
        fontWeight: 600,
      },
    ],
  ];
  const { doc_comparison_id } = state || {};
  const { snackbarState, openSnackbar, handleCloseSnackBar } = useSnackbar();
  const [activeFilters, setActiveFilters] = useState<{
    paramCategory: string | null;
    clause: string | null;
    clauseValue: string | null;
  }>({ paramCategory: null, clause: null, clauseValue: null });

  // States
  const [loading, setLoading] = useState(true);
  const [loader] = useState<any>(<Loader />);
  const [comparisonData, setComparisonData] = useState<any>({});
  const [filteredMatrixData, setFilteredMatrixData] = useState<any[]>([]);
   
  const [matrixSortConfig, setMatrixSortConfig] = useState<{
    fieldName: string;
    direction: 'asc' | 'desc' | null;
  }>({ fieldName: '', direction: null });
  const [matrixColumns, setMatrixColumns] = useState<MatrixColumns[]>([]);
  const [openDocument, setOpenDocument] = useState<boolean>(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const documentsRef = useRef<any[]>([]);
  const [
    documentDetails,
    setDocumentDetails,
  ] = useState<DocumentDetailsDataType>({
    documentUrl: '',
    chunks: [],
    pageNumber: 0,
  });
  const [filterResetKey, setFilterResetKey] = useState(0);
  const generateMatrixColumns = (contractKeys: string[]): MatrixColumns[] => {
    const defaultMatrixColumns: MatrixColumns[] = [
      {
        header: 'Provision Name',
        accessor: 'provision',
        isSortEnabled: false,
        cell: (value: any, row: any) => (
          <div className={classes.provisionGrid}>
            <div>{value}</div>
            {row?.tooltip?.description && (
              <Tooltip
                title={
                  <>
                    <Typography
                      fontSize="13px"
                      lineHeight="19.5px"
                      fontType="semiBold"
                      textColor={patientAccessTheme.palette.status.white}
                    >
                      {row?.tooltip?.description}
                    </Typography>
                    <Typography
                      fontSize="13px"
                      lineHeight="19.5px"
                      fontType="regular"
                      textColor={patientAccessTheme.palette.status.white}
                      margin="8px 0px 0px 0px"
                    >
                      {row?.tooltip?.explaination?.replace(/\?/g, '')}
                    </Typography>
                  </>
                }
                placement="bottom"
              >
                <div className={classes.iconWrapper}>
                  <SvgInfo />
                </div>
              </Tooltip>
            )}
          </div>
        ),
      },
    ];

    const dynamicColumns: MatrixColumns[] = contractKeys?.map((key) => ({
      header: key,
      accessor: key,
      align: 'center',
      cell: getBooleanCell,
    }));

    return [...defaultMatrixColumns, ...dynamicColumns];
  };

  const generateMatrixRows = (
    comparison_parameters: any[],
    contractKeys: string[],
  ) => {
    const rows: any[] = [];

    comparison_parameters?.forEach((param) => {
      param?.subParameter?.forEach((sub: any) => {
        const row: any = {
          provision: sub?.name,
          favorabilityRange: sub?.favorability_range || '-',
          status: sub?.favorability_range,
          ...(sub?.chunks?.length > 0
            ? {}
            : {
                tooltip: {
                  description: sub?.description || '',
                  explaination: sub?.explanation || '',
                },
              }),
          background: {},
          category: param?.parameter,
          paramCategory: param?.category,
        };

         
        contractKeys?.forEach((key) => {
          let value = '';
          const category = param?.category;
          const rawValue = sub[key]?.value;
          if (rawValue === null || rawValue === undefined || rawValue === '') {
            value = '-';
          } else if (typeof rawValue === 'boolean') {
            if (category === 'Red Flag Checks') {
              value = rawValue ? 'Flagged' : 'No';
            } else if (category === 'Missing Checks') {
              value = rawValue ? 'Yes' : 'Missing';
            } else {
              value = rawValue ? 'Yes' : 'No';
            }
          } else {
            value = rawValue;
          }
          row[`${key}_raw`] = sub[key]?.value ?? null;
          row[key] = (
            <Box key={`${key}-value`} className={classes.parameterBox}>
              <Typography fontSize="14px" lineHeight="24px" component="div">
                <span>{value}</span>
                {sub[key]?.chunks?.length > 0 &&
                  Array.from(
                    new Map(
                       
                      sub[key]?.chunks.map((ref: any) => {
                        const key =
                          ref?.grounding?.extractions?.reference ??
                          ref?.grounding?.page;

                        return [key, ref];
                      }),
                    ).values(),
                     
                  ).map(
                     
                    (ref: any, ind: number, arr: any[]) => {
                      const reference = ref?.grounding?.extractions?.reference;
                      const page = ref?.grounding?.page;
                      if (reference || (page !== null && page !== undefined)) {
                        return (
                          <DottedText
                            text={
                              reference
                                ? `${reference}`
                                : `PgNo:${page + 1}${
                                    ind < arr.length - 1 ? ',' : ''
                                  }`
                            }
                            styles={{ paddingLeft: '5px' }}
                             
                            onClick={() =>
                              onClickReference(
                                sub[key].contract_id,
                                sub[key].chunks,
                                page,
                              )
                            }
                          />
                        );
                      }
                      return null;
                    },
                  )}
              </Typography>
            </Box>
          );
          // row.tooltip[key] = sub[key]?.evidence?.[0] || null;
          row.background[key] = getProvisonCellBackground(
            sub[key]?.favorability,
            sub[key]?.value,
          );
        });
        rows.push(row);
      });
    });

    return rows;
  };

  const onClickReference = async (
    contract_id: string,
    chunks: any[],
    pageNumber: number,
  ) => {
    const selectedDoc = documentsRef.current.find(
      (temp: any) => temp.effective_id === contract_id,
    );
    let documentUrl = selectedDoc?.blobUrl ?? null;
    if (!documentUrl) {
      documentUrl = await createBlobUrl(selectedDoc?.document_url);
      setDocuments((prevDocs: any[]) =>
        prevDocs.map((doc) =>
          doc.contract_id === contract_id
            ? { ...doc, blobUrl: documentUrl }
            : doc,
        ),
      );
    }
    setDocumentDetails({ documentUrl, chunks, pageNumber });
    setOpenDocument(true);
  };
  const handleMatrixCSVDownload = () => {
    handleDownloadCSV(
      `${
        properties.aiqBaseUrl + properties.contractBaseUrl
      }/doc-comparison-csv`,
      { comparisonData },
      `${comparisonData?.comparison_name}.csv`,
      openSnackbar,
    );
  };

  // Apis
  const getMatrixData = async () => {
    try {
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getDocComparison,
        {
          comparison_id: doc_comparison_id,
        },
      );
      const data = response?.data?.data || {};
      const matrixColumns = generateMatrixColumns(
        data?.documents?.map(
          (doc: Contract) =>
            `${doc.is_draft ? 'Draft' : doc?.vendor_name} - ${
              doc.employer_name
            }`,
        ) || [],
      );
      setMatrixColumns(matrixColumns);
      setDocuments(data.contract_documents);
      setComparisonData(data);
    } catch (error: any) {
      openSnackbar(error?.response?.data?.message, 'error', 2000);
    } finally {
      setLoading(false);
    }
  };
  const applyFilters = (filters: {
    paramCategory: string | null;
    clause: string | null;
    clauseValue: string | null;
  }) => {
    if (!comparisonData) {
      setFilteredMatrixData([]);
      return;
    }
    const contractKeys =
      comparisonData?.documents?.map(
        (doc: Contract) =>
          `${doc.is_draft ? 'Draft' : doc?.vendor_name} - ${
            doc?.employer_name
          }`,
      ) || [];

    let filteredParams = comparisonData?.comparison_parameters || [];

    if (filters.paramCategory) {
      filteredParams = filteredParams.filter(
        (param: any) =>
          param?.category?.trim() === filters.paramCategory?.trim(),
      );
    }

    if (filters.clause) {
      filteredParams = filteredParams.filter(
        (param: any) => param?.parameter?.trim() === filters.clause?.trim(),
      );
    }

    const tableRows = generateMatrixRows(filteredParams, contractKeys);

    if (filters.clauseValue) {
      const filteredRows = tableRows.filter((row) => {
        const category = row.paramCategory;
        const allContractsHaveValue = contractKeys.every(
          (key: string) =>
            row.hasOwnProperty(`${key}_raw`) &&
            row[`${key}_raw`] !== undefined &&
            row[`${key}_raw`] !== null,
        );

        if (!allContractsHaveValue) {
          return false;
        }

        if (category === 'Red Flag Checks') {
          if (filters.clauseValue === 'Flagged') {
            // ALL must be strictly true
            const result = contractKeys.every(
              (key: string) => row[`${key}_raw`] === true,
            );
            return result;
          }
          if (filters.clauseValue === 'Not Flagged') {
            // ALL must be strictly false
            const result = contractKeys.every(
              (key: string) => row[`${key}_raw`] === false,
            );
            return result;
          }
        }

        if (category === 'Missing Checks') {
          if (filters.clauseValue === 'Flagged') {
            // Missing = ALL must be strictly false
            const result = contractKeys.every(
              (key: string) => row[`${key}_raw`] === false,
            );
            return result;
          }
          if (filters.clauseValue === 'Not Flagged') {
            // Present = ALL must be strictly true
            const result = contractKeys.every(
              (key: string) => row[`${key}_raw`] === true,
            );
            return result;
          }
        }

        // ✅ Key Terms and all other categories
        if (filters.clauseValue === 'Flagged') {
          // ALL must be strictly true
          const result = contractKeys.every(
            (key: string) => row[`${key}_raw`] === true,
          );
          return result;
        }

        if (filters.clauseValue === 'Not Flagged') {
          // ALL must be strictly false
          const result = contractKeys.every(
            (key: string) => row[`${key}_raw`] === false,
          );
          return result;
        }
        return false;
      });
      setFilteredMatrixData(filteredRows);
      return;
    }

    setFilteredMatrixData(tableRows);
  };

  // UseEffects
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  useEffect(() => {
    getMatrixData();
  }, []);
  useEffect(() => {
    if (Object.keys(comparisonData).length > 0) {
      applyFilters(activeFilters);
    }
  }, [activeFilters, comparisonData]);
  const filterOptions: FilterOption[] = React.useMemo(() => {
    const categoryOptions = Array.from(
      new Set(
        comparisonData?.comparison_parameters?.map(
          (param: any) => param?.category,
        ) || [],
      ),
    ).filter(Boolean) as string[];
    const showStatusLevel = activeFilters.paramCategory !== 'Key Terms';

    return [
      {
        label: 'Category',
        options: [],
        selected: activeFilters.paramCategory,
        onChange: (selections: string[]) => {
          setActiveFilters({
            paramCategory: selections?.[0] || null,
            clause: null,
            clauseValue:
              selections?.[0] === 'Key Terms' ? null : selections?.[1] || null,
          });
        },
        levels: [
          {
            label: 'Select Category',
            options: categoryOptions,
            isMultiSelect: false,
          },
          ...(showStatusLevel
            ? [
                {
                  label: 'Select Status',
                  options: ['Flagged', 'Not Flagged'],
                  isMultiSelect: false,
                },
              ]
            : []),
        ],
      },

      {
        label: 'Clauses',
        options: [],
        selected: activeFilters.clause,
        onChange: (selections: string[]) => {
          setActiveFilters({
            clause: selections?.[0] || null,
            paramCategory: null,
            clauseValue: null,
          });
        },
        levels: [
          {
            label: 'Select Clause',
            options: Array.from(
              new Set(
                comparisonData?.comparison_parameters?.map(
                  (param: any) => param?.parameter,
                ) || [],
              ),
            ).filter(Boolean) as string[],
            isMultiSelect: false,
          },
        ],
      },
    ];
  }, [comparisonData, activeFilters]);

  return loading ? (
    loader
  ) : (
    <>
      <Grid className={classes.breadCrumbsGrid}>
        <Breadcrumbs itemList={breadCrumpItemList} />
      </Grid>
      <hr className={classes.hr} />
      <Grid className={classes.root}>
        <Grid
          className={classes.leftView}
          style={{ width: openDocument ? '55%' : '100%' }}
        >
          <Typography
            fontSize="24px"
            fontType="semiBold"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin="0px 0px 14px 0px"
          >
            {comparisonData?.comparison_name}
          </Typography>
          <Grid className={classes.vendorContainer}>
            <Grid>
              <Typography
                fontType="regular"
                fontSize="12px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                margin="0px 0px 8px 0px"
              >
                Vendor Type
              </Typography>
              <Typography
                fontType="medium"
                fontSize="14px"
                textColor={patientAccessTheme.palette.text.highemphasis}
              >
                {comparisonData?.vendor_type}
              </Typography>
            </Grid>
            <Button
              variant="contained"
              label={'Download CSV Report'}
              height="36px"
              width="auto"
              onClick={handleMatrixCSVDownload}
            />
          </Grid>
          <hr className={classes.hr} />

          <CardComponent
            title={'Comparison Matrix'}
            subTitle={
              'Matrix of provisions present and missing in the agreement'
            }
            mainGridStyles={{ marginTop: '20px' }}
            topRightItem={
              <Grid
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <DynamicFilter
                  filterOptions={filterOptions}
                  resetKey={filterResetKey}
                />
                <Button
                  label={'Reset Filters'}
                  onClick={() => {
                    setActiveFilters({
                      paramCategory: null,
                      clause: null,
                      clauseValue: null,
                    });
                    setFilterResetKey((prev) => prev + 1);
                  }}
                />
              </Grid>
            }
            body={
              <Grid style={{ marginTop: '8px' }}>
                {filteredMatrixData?.length > 0 ? (
                  <MatrixTable
                    columns={matrixColumns}
                    rowsData={filteredMatrixData}
                    handleSort={() => {}}
                    sortConfig={matrixSortConfig}
                    showAddMore={false}
                    groupByCategory={true}
                  />
                ) : (
                  <NoDataVisual text="We couldn't find any data that matches the selected parameters and confidence scores" />
                )}
              </Grid>
            }
          />
        </Grid>
        {openDocument && (
          <Grid className={classes.rightView}>
            <PdfHighlighter
              key={`${documentDetails?.documentUrl}-${documentDetails?.pageNumber}-${documentDetails?.chunks?.length}`}
              pdfUrl={documentDetails?.documentUrl}
              chunks={documentDetails?.chunks}
              highlightIds={documentDetails?.chunks.map(
                (item: any) => item.chunk_id,
              )}
              pageNumber={documentDetails?.pageNumber}
              contentStyles={{ height: '610px' }}
              onClose={() => setOpenDocument(false)}
            />
          </Grid>
        )}
      </Grid>
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
});

export default DocumentComparisonInternalPage;
